import React, { useEffect, useState, useRef, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Search } from 'lucide-react';
import { FaArrowLeft, FaArrowRight, FaCheck, FaTimes, FaSpinner, FaThumbsDown, FaThumbsUp } from 'react-icons/fa';
import axios from 'axios';
import { useAuth0 } from '@auth0/auth0-react';
import CalendarCell from './CalendarCell';
import { useNavigate } from 'react-router-dom';
import { IoCalendarOutline, IoSettingsOutline, IoLogOutOutline } from 'react-icons/io5';
import { RiArticleLine } from 'react-icons/ri';
import logo from '../Images/logo.png';
import { FaRegQuestionCircle } from "react-icons/fa";
import ReactDOM from 'react-dom';
import { IoMdInformationCircle } from "react-icons/io";
import useUserStore from '../store';
import moment from 'moment';
import { FaRocket, FaStar, FaArrowUp, FaPlusCircle } from 'react-icons/fa';
import { loadStripe } from '@stripe/stripe-js';

const stripePromise = loadStripe(`${process.env.REACT_APP_STRIPE_PROMISE}`); // Replace with your actual publishable key

const Banner = ({ text, buttonText, onButtonClick }) => {
    return (
        <div className="bg-gradient-to-r from-blue-500 to-purple-500 text-white px-4 py-2 rounded-lg flex justify-between items-center mt-4">
            <div className="flex items-center gap-2">
                <IoMdInformationCircle className="text-white text-lg" />
                <p className="text-sm font-medium">{text}</p>
            </div>
        </div>
    );
};

const HowItWorksModal = ({ isOpen, onClose }) => {
    if (!isOpen) return null;

    return ReactDOM.createPortal(
        <div className="fixed inset-0 z-50 bg-black bg-opacity-75 flex items-center justify-center p-4">
            <div className="bg-gray-800 rounded-xl shadow-lg p-6 w-full max-w-lg text-white">
                <h2 className="text-2xl font-extrabold mb-4 flex items-center gap-2">
                    📅 Welcome to Your Content Calendar!
                </h2>
                <p className="text-gray-300 mb-4 leading-relaxed">
                    This is your <strong>go-to hub</strong> for tracking all your drafts and scheduled articles <strong>at a glance</strong>.
                    Think of it as your <strong>content planning HQ!</strong> 🚀
                </p>
                <div className="mb-4">
                    <p className="text-lg font-semibold text-gray-100 mb-2">✨ <strong>How to navigate it:</strong></p>
                    <ul className="space-y-3">
                        <li className="flex items-start gap-2">
                            📆 <span><strong>See Your Schedule:</strong> View all your drafts and scheduled articles <strong>in a calendar format</strong>.</span>
                        </li>
                        <li className="flex items-start gap-2">
                            🔍 <span><strong>Explore Specific Days:</strong> Click on any date to <strong>see the drafts</strong> scheduled for that day.</span>
                        </li>
                        <li className="flex items-start gap-2">
                            ✍️ <span><strong>Review & Accept:</strong> Articles marked <strong>incomplete</strong> are still in the <strong>idea phase</strong>.
                                To view a draft, go to the <strong>article table view</strong> and approve it.</span>
                        </li>
                    </ul>
                </div>
                <button
                    onClick={onClose}
                    className="w-full bg-blue-500 text-white py-3 rounded-lg hover:bg-blue-600 transition-all font-semibold"
                >
                    Got it! ✅
                </button>
            </div>
        </div>,
        document.body
    );
};

const Calendar = ({ onArticleClick }) => {
    const {
        userId,
        setUserId,
        blogs,
        setBlogs,
        drafts,
        setDrafts,
        keywordData,
        setKeywordData,
        loading,
        setLoading,
        error,
        setError,
    } = useUserStore();
    const [currentDate, setCurrentDate] = useState(new Date());
    const { user, isAuthenticated, logout } = useAuth0();
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const navigate = useNavigate();
    const [activePage, setActivePage] = useState('contentCalendar');
    const [isHowItWorksOpen, setIsHowItWorksOpen] = useState(false);
    const [monthsToRender, setMonthsToRender] = useState([new Date()]);
    const calendarRef = useRef(null);
    const [isPaid, setIsPaid] = useState(false);
    const [subscriptionEndDate, setSubscriptionEndDate] = useState(null);
    const [loadingUpgrade, setLoadingUpgrade] = useState(false);
    const [processingTasks, setProcessingTasks] = useState({});
    const [showFeedbackModal, setShowFeedbackModal] = useState(false);
    const [feedbackText, setFeedbackText] = useState('');
    const [currentArticleId, setCurrentArticleId] = useState(null);

    const NavItem = ({ label, icon, isActive, onClick }) => {
        return (
            <button
                onClick={onClick}
                className={`flex sm:justify-start justify-start items-center gap-3 p-3 w-full rounded-md hover:bg-slate-800/50 ${
                    isActive ? 'bg-slate-800/50' : ''
                }`}
            >
                {icon}
                <span className="text-sm text-slate-300 hover:text-white">{label}</span>
            </button>
        );
    };

    const saveTasksToCache = (tasks) => {
        localStorage.setItem('processingTasks', JSON.stringify(tasks));
    };

    const loadTasksFromCache = () => {
        const cachedTasks = localStorage.getItem('processingTasks');
        return cachedTasks ? JSON.parse(cachedTasks) : {};
    };

    useEffect(() => {
        const cachedTasks = loadTasksFromCache();
        const now = Date.now();
        const validTasks = {};

        for (const draftId in cachedTasks) {
            const task = cachedTasks[draftId];
            if (task && task.timestamp && (now - task.timestamp) <= 15 * 60 * 1000) {
                validTasks[draftId] = task;
            }
        }

        Object.keys(validTasks).forEach(draftId => {
            const task = validTasks[draftId];
            if (task && task.taskId) {
                const messages = task.action === 'accept'
                    ? ['Researching Reddit...', 'Creating Outline...', 'Drafting Content...', 'Finalizing Article...']
                    : ['Analyzing Pitch...', 'Identifying Issues...', 'Preparing New Draft...', 'Finalizing Pitch...'];
                startPolling(draftId, task.taskId, task.action, messages, task.messageIndex || 0);
            }
        });
        setProcessingTasks(validTasks);
    }, []);

    const clearExpiredTasks = () => {
        const cachedTasks = loadTasksFromCache();
        const now = Date.now();
        let hasChanges = false;
        const validTasks = {};

        for (const draftId in cachedTasks) {
            const task = cachedTasks[draftId];
            if (task && task.timestamp && (now - task.timestamp) <= 15 * 60 * 1000) {
                validTasks[draftId] = task;
            } else {
                hasChanges = true;
            }
        }

        if (hasChanges) {
            saveTasksToCache(validTasks);
            setProcessingTasks(validTasks);
        }
    };

    useEffect(() => {
        const intervalId = setInterval(clearExpiredTasks, 60 * 1000);
        return () => clearInterval(intervalId);
    }, []);

    useEffect(() => {
        const fetchBlogsAndDrafts = async () => {
            if (!userId) {
                console.warn("userId is not yet available in the store.");
                return;
            }

            setLoading(true);
            setError(null);
            try {
                const userResponse = await axios.get(`https://trendhopper-c3934b7293e6.herokuapp.com/get-user/${user.email}/`);
                if (userResponse.data.stripe_customer_id) {
                    setIsPaid(true);
                } else {
                    setIsPaid(false);
                }
                if (userResponse.data.subscription_end_date) {
                    setSubscriptionEndDate(userResponse.data.subscription_end_date);
                    const endDate = moment(userResponse.data.subscription_end_date);
                    const now = moment();
                    if (now.isAfter(endDate)) {
                        setIsPaid(false);
                    }
                }

                const blogResponse = await fetch(`https://trendhopper-c3934b7293e6.herokuapp.com/user/${userId}/blogs/`);
                if (!blogResponse.ok) throw new Error('Failed to fetch blogs');
                const blogData = await blogResponse.json();
                setBlogs(blogData.blogs);

                const draftData = {};
                for (const blog of blogData.blogs) {
                    const draftResponse = await fetch(`https://trendhopper-c3934b7293e6.herokuapp.com/drafts/${blog.id}/`);
                    if (draftResponse.ok) {
                        const draftResponseData = await draftResponse.json();
                        draftData[blog.id] = draftResponseData.drafts;
                    }
                }

                setDrafts(draftData);
                const allKeywords = {};
                for (const blogId in draftData) {
                    for (const draft of draftData[blogId]) {
                        const keywordResponse = await axios.get(`https://trendhopper-c3934b7293e6.herokuapp.com/draft/${draft.id}/keywords`);
                        allKeywords[draft.id] = keywordResponse.data.keywords;
                    }
                }
                setKeywordData(allKeywords);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchBlogsAndDrafts();
    }, [userId, setBlogs, setDrafts, setKeywordData, setLoading, setError]);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const getDaysInMonth = (month, year) => new Date(year, month, 0).getDate();
    const getFirstDayOfMonth = (month, year) => {
        const firstDay = new Date(year, month - 1, 1).getDay();
        return (firstDay === 0 ? 6 : firstDay - 1);
    };
    const currentMonth = currentDate.getMonth();
    const currentYear = currentDate.getFullYear();
    const daysInCurrentMonth = getDaysInMonth(currentMonth + 1, currentYear);
    const firstDayOfCurrentMonth = getFirstDayOfMonth(currentMonth + 1, currentYear);

    const allDrafts = Object.values(drafts).flat();

    const handlePrevMonth = () => {
        setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
    };

    const handleNextMonth = () => {
        setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
    };

    const mobileDaysInMonth = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth() + 1,
        0
    ).getDate();

    const mobileFirstDayOfMonth = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        1
    ).getDay();

    const monthYear = currentDate.toLocaleString('default', { month: 'long', year: 'numeric' });
    const weekDays = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

    const selectedDayDrafts = allDrafts.filter(draft => {
        const draftDate = new Date(draft.date);
        return draftDate.getDate() === currentDate.getDate() &&
            draftDate.getMonth() === currentDate.getMonth() &&
            draftDate.getFullYear() === currentDate.getFullYear();
    });

    const cells = [];
    for (let i = 0; i < firstDayOfCurrentMonth; i++) {
        cells.push(<div key={`empty-${i}`} className="min-h-[100px] sm:min-h-[120px]" />);
    }

    for (let day = 1; day <= daysInCurrentMonth; day++) {
        const draftForDay = allDrafts.find(draft => {
            const draftDate = new Date(draft.date);
            return draftDate.getDate() === day && draftDate.getMonth() === currentMonth && draftDate.getFullYear() === currentYear;
        });

        const dayData = draftForDay
            ? {
                title: draftForDay.title,
                status: draftForDay.status === 0 ? "incomplete" : draftForDay.status === 1 ? "in progress" : "done",
                articleId: draftForDay.id,
                type: draftForDay.type || "unknown",
            }
            : null;

        cells.push(
            <CalendarCell
                key={day}
                day={day}
                data={dayData}
                onClick={() => {
                    if (draftForDay && draftForDay.status !== 0) {
                        onArticleClick(draftForDay.id);
                    }
                }}
                disabled={draftForDay?.status === 0}
                keywords={keywordData[draftForDay?.id] || []}
                processing={processingTasks[draftForDay?.id]}
                onAccept={() => handleAcceptReject(draftForDay.id, 'accept')}
                onReject={() => handleOpenFeedbackModal(draftForDay.id)}
            />
        );
    }

    useEffect(() => {
        const handleScroll = () => {
            const { scrollTop, clientHeight, scrollHeight } = calendarRef.current;
            if (scrollTop + clientHeight >= scrollHeight - 10) {
                if (monthsToRender.length < 3) {
                    const lastMonth = monthsToRender[monthsToRender.length - 1];
                    const nextMonth = new Date(
                        lastMonth.getFullYear(),
                        lastMonth.getMonth() + 1,
                        1
                    );
                    setMonthsToRender((prev) => [...prev, nextMonth]);
                }
            }
        };

        if (calendarRef.current) {
            calendarRef.current.addEventListener("scroll", handleScroll);
        }
        return () => {
            if (calendarRef.current) {
                calendarRef.current.removeEventListener("scroll", handleScroll);
            }
        };
    }, [monthsToRender]);

    const handleUpgradeClick = async () => {
        setLoadingUpgrade(true);
        setError(null);

        try {
            const response = await axios.post(
                'https://trendhopper-c3934b7293e6.herokuapp.com/create-checkout-session/',
                { email: user.email },
                { headers: { 'Content-Type': 'application/json' } }
            );

            const session = response.data;

            if (session && session.id) {
                const stripe = await stripePromise;
                const { error } = await stripe.redirectToCheckout({
                    sessionId: session.id,
                });

                if (error) {
                    console.error('Error redirecting to checkout:', error);
                    setError('Failed to redirect to checkout.');
                }
            } else {
                setError('Failed to create Stripe checkout session.');
            }
        } catch (err) {
            if (err.response && err.response.data && err.response.data.error) {
                setError(`Server Error: ${err.response.data.error}`);
                console.error('Error during upgrade:', err.response.data.error);
            } else if (err.request) {
                setError('No response received from server.');
                console.error('Error during upgrade: No response from server', err.request);
            } else {
                setError('Failed to initiate upgrade process.');
                console.error('Error during upgrade:', err);
            }
        } finally {
            setLoadingUpgrade(false);
        }
    };

    const renderMonth = (date) => {
        const month = date.getMonth();
        const year = date.getFullYear();
        const daysInMonth = getDaysInMonth(month + 1, year);
        const firstDay = getFirstDayOfMonth(month + 1, year);

        const cells = [];

        for (let i = 0; i < firstDay; i++) {
            cells.push(
                <div key={`empty-${month}-${i}`} className="min-h-[100px] sm:min-h-[120px]" />
            );
        }

        for (let day = 1; day <= daysInMonth; day++) {
            const isCurrentDay =
                day === new Date().getDate() &&
                month === new Date().getMonth() &&
                year === new Date().getFullYear();

            const draftForDay = allDrafts.find(draft => {
                const draftDate = new Date(draft.date);
                return draftDate.getDate() === day && draftDate.getMonth() === month && draftDate.getFullYear() === year;
            });

            const dayData = draftForDay
                ? {
                    title: draftForDay.title,
                    status: draftForDay.status === 0 ? "incomplete" : draftForDay.status === 1 ? "in progress" : "done",
                    articleId: draftForDay.id,
                    type: draftForDay.type || "unknown",
                }
                : null;

            cells.push(
                <CalendarCell
                    key={`${month}-${year}-${day}`}
                    day={day}
                    data={dayData}
                    onClick={() => {
                        if (draftForDay && draftForDay.status !== 0) {
                            onArticleClick(draftForDay.id);
                        }
                    }}
                    disabled={draftForDay?.status === 0}
                    keywords={keywordData[draftForDay?.id] || []}
                    isCurrentDay={isCurrentDay}
                    processing={processingTasks[draftForDay?.id]}
                    onAccept={() => handleAcceptReject(draftForDay.id, 'accept')}
                    onReject={() => handleOpenFeedbackModal(draftForDay.id)}
                />
            );
        }

        const remainingCells = 35 - cells.length;
        for (let i = 0; i < remainingCells; i++) {
            cells.push(
                <div
                    key={`empty-post-${month}-${i}`}
                    className="min-h-[100px] sm:min-h-[120px]"
                />
            );
        }

        return (
            <div key={`${month}-${year}`} className="mb-8">
                <h2 className="text-2xl text-white mb-4">
                    {date.toLocaleString("default", { month: "long" })} {year}
                </h2>
                <div className="grid grid-cols-7 gap-px bg-slate-700 p-px rounded-lg overflow-hidden">
                    {cells}
                </div>
            </div>
        );
    };

    const toggleSidebar = () => {
        setIsSidebarOpen(!isSidebarOpen);
    };

    const onSelect = (page) => {
        setActivePage(page);
        navigate(`/${page.replace(/([A-Z])/g, '-$1').toLowerCase()}`);
        setIsSidebarOpen(false);
    };

    const calculateAggregatedKeywords = (draftId) => {
        const keywords = keywordData[draftId] || [];
        if (keywords.length === 0) {
            return {
                totalSearchVolume: 0,
                averageKeywordDifficulty: 0,
                averageCpc: 0,
            };
        }

        const totalSearchVolume = keywords.reduce((sum, keyword) => sum + (keyword.search_volume_msv || 0), 0);
        const totalKeywordDifficulty = keywords.reduce((sum, keyword) => sum + (keyword.keyword_difficulty || 0), 0);
        const totalCpc = keywords.reduce((sum, keyword) => sum + (keyword.cpc || 0), 0);
        const averageKeywordDifficulty = keywords.length > 0 ? totalKeywordDifficulty / keywords.length : 0;
        const averageCpc = keywords.length > 0 ? totalCpc / keywords.length : 0;

        return {
            totalSearchVolume,
            averageKeywordDifficulty: Math.round(averageKeywordDifficulty),
            averageCpc: averageCpc.toFixed(2),
        };
    };

    const handleAcceptReject = async (articleId, action) => {
        const isAccept = action === 'accept';
        const messages = isAccept
            ? ['Researching Reddit...', 'Creating Outline...', 'Drafting Content...', 'Finalizing Article...']
            : ['Analyzing Pitch...', 'Identifying Issues...', 'Preparing New Draft...', 'Finalizing Pitch...'];
        const url = isAccept
            ? `https://trendhopper-c3934b7293e6.herokuapp.com/accept-draft/${articleId}/`
            : `https://trendhopper-c3934b7293e6.herokuapp.com/reject-draft/${articleId}/`;

        setProcessingTasks(prevTasks => {
            const newTasks = {
                ...prevTasks,
                [articleId]: { status: 'pending', message: messages[0], timestamp: Date.now() }
            };
            saveTasksToCache(newTasks);
            return newTasks;
        });

        try {
            const response = await axios.get(url);
            const { task_id } = response.data;

            setProcessingTasks(prevTasks => {
                const newTasks = {
                    ...prevTasks,
                    [articleId]: { status: 'pending', message: messages[0], taskId: task_id, action: action, messageIndex: 0, timestamp: Date.now() }
                };
                saveTasksToCache(newTasks);
                return newTasks;
            });

            startPolling(articleId, task_id, action, messages, 0);

        } catch (err) {
            console.error('Error processing task:', err);
            setError(`Error processing article ${articleId}. Please try again.`);

            setProcessingTasks(prevTasks => {
                const { [articleId]: removed, ...rest } = prevTasks;
                saveTasksToCache(rest);
                return rest;
            });
        }
    };

    const startPolling = async (draftId, taskId, action, messages, initialMessageIndex = 0) => {
        let taskStatus = 'PENDING';
        let step = initialMessageIndex;

        const intervalId = setInterval(() => {
            step = (step + 1) % messages.length;
            setProcessingTasks(prevTasks => {
                const newTasks = {
                    ...prevTasks,
                    [draftId]: { status: 'pending', message: messages[step], taskId: taskId, action: action, messageIndex: step, timestamp: Date.now() }
                };
                return newTasks;
            });
        }, 25000);

        while (taskStatus === 'PENDING') {
            try {
                const statusResponse = await axios.get(`https://trendhopper-c3934b7293e6.herokuapp.com/app/check-task-status/${taskId}`);
                taskStatus = statusResponse.data.status;
                if (taskStatus !== 'PENDING') break;
                await new Promise(resolve => setTimeout(resolve, 5000));
            } catch (error) {
                console.error("Error checking task status:", error);
                clearInterval(intervalId);
                setProcessingTasks(prevTasks => {
                    const { [draftId]: removed, ...rest } = prevTasks;
                    return rest;
                });
                const cachedTasks = loadTasksFromCache();
                const { [draftId]: taskToRemove, ...updatedTasks } = cachedTasks;
                saveTasksToCache(updatedTasks);
                return;
            }
        }

        clearInterval(intervalId);

        if (userId) {
            const fetchBlogsAndDrafts = async () => {
                if (!userId) {
                    console.warn("userId is not yet available in the store.");
                    return;
                }

                setError(null);
                try {
                    const blogResponse = await fetch(`https://trendhopper-c3934b7293e6.herokuapp.com/user/${userId}/blogs/`);
                    if (!blogResponse.ok) throw new Error('Failed to fetch blogs');
                    const blogData = await blogResponse.json();
                    setBlogs(blogData.blogs);

                    const draftData = {};
                    for (const blog of blogData.blogs) {
                        const draftResponse = await fetch(`https://trendhopper-c3934b7293e6.herokuapp.com/drafts/${blog.id}/`);
                        if (draftResponse.ok) {
                            const draftResponseData = await draftResponse.json();
                            draftData[blog.id] = draftResponseData.drafts;
                        }
                    }

                    setDrafts(draftData);
                    const allKeywords = {};
                    for (const blogId in draftData) {
                        for (const draft of draftData[blogId]) {
                            const keywordResponse = await axios.get(`https://trendhopper-c3934b7293e6.herokuapp.com/draft/${draft.id}/keywords`);
                            allKeywords[draft.id] = keywordResponse.data.keywords;
                        }
                    }
                    setKeywordData(allKeywords);
                } catch (err) {
                    setError(err.message);
                }
            };
            fetchBlogsAndDrafts();
        }
        setProcessingTasks(prevTasks => {
            const { [draftId]: removed, ...rest } = prevTasks;
            return rest;
        });
        const cachedTasks = loadTasksFromCache();
        const { [draftId]: taskToRemove, ...updatedTasks } = cachedTasks;
        saveTasksToCache(updatedTasks);
    };

    const handleOpenFeedbackModal = (articleId) => {
        setCurrentArticleId(articleId);
        setShowFeedbackModal(true);
    };

    const handleCloseFeedbackModal = () => {
        setShowFeedbackModal(false);
        setFeedbackText('');
        setCurrentArticleId(null);
    };

    const handleFeedbackChange = (e) => {
        setFeedbackText(e.target.value);
    };

    const handleRejectWithFeedback = async () => {
        if (!currentArticleId) return;

        const url = `https://trendhopper-c3934b7293e6.herokuapp.com/reject-draft/${currentArticleId}/`;

        const messages = ['Analyzing Pitch...', 'Identifying Issues...', 'Preparing New Draft...', 'Finalizing Pitch...'];

        setProcessingTasks(prevTasks => {
            const newTasks = {
                ...prevTasks,
                [currentArticleId]: { status: 'pending', message: messages[0], timestamp: Date.now() }
            };
            saveTasksToCache(newTasks);
            return newTasks;
        });

        handleCloseFeedbackModal();

        try {
            const response = await axios.post(url, { feedback: feedbackText });
            const { task_id } = response.data;

            setProcessingTasks(prevTasks => {
                const newTasks = {
                    ...prevTasks,
                    [currentArticleId]: { status: 'pending', message: messages[0], taskId: task_id, action: 'reject', messageIndex: 0, timestamp: Date.now() }
                };
                saveTasksToCache(newTasks);
                return newTasks;
            });

            startPolling(currentArticleId, task_id, 'reject', messages, 0);

        } catch (err) {
            console.error("Error processing task:", err);
            setError(`Error processing article ${currentArticleId}. Please try again.`);
            setProcessingTasks(prevTasks => {
                const { [currentArticleId]: taskToRemove, ...rest } = prevTasks;
                saveTasksToCache(rest);
                return rest;
            });
        }
    };

    return (
        <div className="w-full">
            {isMobile ? (
                <div className="min-h-screen bg-[#051829] text-white p-4 fixed top-0 left-0 right-0 bottom-0">
                    <div
                        className={`flex h-screen flex-col border-r border-slate-800/50 bg-slate-900 p-2 w-48 ${
                            isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
                        } transition-transform duration-300 ease-in-out fixed top-0 left-0 z-10`}
                    >
                        <button
                            onClick={toggleSidebar}
                            className="absolute top-2 left-2 p-2 text-white focus:outline-none"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                className="h-6 w-6"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M15 19l-7-7 7-7"
                                />
                            </svg>
                        </button>
                        <div
                            className="flex items-center mb-4 cursor-pointer mt-6 justify-start"
                            onClick={() => navigate('/articles-table-view')}
                        >
                            <img src={logo} alt="TrendHopper Logo" className="h-10 w-10 mx-0" />
                            <h1 className=" text-lg font-bold text-white ml-2">
                                TrendHopper
                            </h1>
                        </div>
                        <nav className="flex-1 space-y-1">
                            <NavItem
                                label="Content Calendar"
                                icon={<IoCalendarOutline className="h-6 w-6 sm:h-5 sm:w-5" />}
                                isActive={activePage === 'contentCalendar'}
                                onClick={() => onSelect('contentCalendar')}
                            />
                            <NavItem
                                label="Articles"
                                icon={<RiArticleLine className="h-6 w-6 sm:h-5 sm:w-5" />}
                                isActive={activePage === 'articlesTableView'}
                                onClick={() => onSelect('articlesTableView')}
                            />
                            <NavItem
                                label="Settings"
                                icon={<IoSettingsOutline className="h-6 w-6 sm:h-5 sm:w-5" />}
                                isActive={activePage === 'settings'}
                                onClick={() => onSelect('settings')}
                            />
                        </nav>
                        <div className="mt-auto pt-4">
                            <button
                                onClick={() =>
                                    logout({ logoutParams: { returnTo: window.location.origin } })
                                }
                                className="w-full flex items-center justify-start gap-3 p-3 text-slate-300 hover:text-white hover:bg-slate-800/50"
                            >
                                <IoLogOutOutline className="h-6 w-6" />
                                <span className=" text-sm">Logout</span>
                            </button>
                        </div>
                    </div>
                    <button
                        onClick={toggleSidebar}
                        className="absolute top-2 left-2 p-2 text-white focus:outline-none"
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            className="h-6 w-6"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M4 6h16M4 12h16M4 18h16"
                            />
                        </svg>
                    </button>
                    <div className="max-w-md mx-auto">
                        <header className="flex items-center justify-between mb-6">
                            <h1 className="text-xl font-medium text-center flex-grow">Calendar</h1>
                        </header>

                        <div className="mb-6">
                            <div className="flex items-center justify-between mb-4">
                                <button className="bg-transparent border-none p-0 cursor-pointer" onClick={handlePrevMonth}>
                                    <ChevronLeft className="h-6 w-6" />
                                    <span className="sr-only">Previous month</span>
                                </button>
                                <h2 className="text-lg font-medium">{monthYear}</h2>
                                <button className="bg-transparent border-none p-0 cursor-pointer" onClick={handleNextMonth}>
                                    <ChevronRight className="h-6 w-6" />
                                    <span className="sr-only">Next month</span>
                                </button>
                            </div>

                            <div className="grid grid-cols-7 gap-1 text-center mb-2">
                                {weekDays.map((day) => (
                                    <div key={day} className="text-sm text-gray-400">
                                        {day}
                                    </div>
                                ))}
                            </div>

                            <div className="grid grid-cols-7 gap-1 text-center">
                                {Array.from({ length: mobileFirstDayOfMonth }).map((_, index) => (
                                    <div key={`empty-${index}`} className="p-2" />
                                ))}
                                {Array.from({ length: mobileDaysInMonth }).map((_, index) => {
                                    const day = index + 1;
                                    const isSelected = day === currentDate.getDate();
                                    const hasDraft = allDrafts.some(draft => {
                                        const draftDate = new Date(draft.date);
                                        return draftDate.getDate() === day &&
                                            draftDate.getMonth() === currentDate.getMonth() &&
                                            draftDate.getFullYear() === currentDate.getFullYear();
                                    });
                                    const draftForDay = allDrafts.find(draft => {
                                        const draftDate = new Date(draft.date);
                                        return draftDate.getDate() === day &&
                                            draftDate.getMonth() === currentDate.getMonth() &&
                                            draftDate.getFullYear() === currentDate.getFullYear();
                                    });
                                    return (
                                        <div
                                            key={day}
                                            className={`p-2 rounded-full w-10 h-10 flex items-center justify-center cursor-pointer
                                                ${isSelected ? "bg-orange-500" : ""}
                                                ${hasDraft && !isSelected && draftForDay?.status !== 0 ? "bg-blue-500 hover:bg-blue-600" : ""}
                                                ${hasDraft && !isSelected && draftForDay?.status === 0 ? "bg-red-500/20 cursor-not-allowed" : ""}`}
                                            onClick={() => {
                                                setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth(), day));
                                            }}
                                        >
                                            {day}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                        {selectedDayDrafts.length > 0 && (
                            <div className="mt-4">
                                <h3 className="text-lg font-semibold mb-2">Drafts for {currentDate.toLocaleDateString()}</h3>
                                <ul>
                                    {selectedDayDrafts.map(draft => {
                                        const statusText = draft.status === 0 ? "Incomplete" : draft.status === 1 ? "In Progress" : "Done";
                                        const { totalSearchVolume, averageKeywordDifficulty, averageCpc } = calculateAggregatedKeywords(draft.id);
                                        return (
                                            <li key={draft.id} className="bg-gray-800 p-3 rounded mb-2 flex flex-col">
                                                <div
                                                    onClick={() => {
                                                        if (draft.status !== 0) {
                                                            onArticleClick(draft.id);
                                                        }
                                                    }}
                                                    className={`cursor-pointer ${draft.status === 0 ? 'cursor-not-allowed' : ''}`}
                                                >
                                                    <p className="font-medium">{draft.title}</p>
                                                    <p className="text-sm text-gray-400">Status: {statusText}</p>
                                                </div>
                                                <div className="flex gap-2 mt-1 items-center">
                                                    <span className="text-xs text-gray-400">
                                                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-4 h-4 inline-block mr-1">
                                                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                        </svg>
                                                        {totalSearchVolume}
                                                    </span>
                                                    <span className="text-xs text-gray-400">
                                                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-4 h-4 inline-block mr-1">
                                                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75l3 3m0 0l3-3m-3 3v-7.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                        </svg>
                                                        {averageKeywordDifficulty}
                                                    </span>
                                                    <span className="text-xs text-gray-400">
                                                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-4 h-4 inline-block mr-1">
                                                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 8.25v-1.5m0 1.5c-1.354 0-2.697.056-4.024.166C6.845 8.51 6 9.47 6 10.5v3.75m0 5.25v-1.5m0 1.5c1.354 0 2.697-.056 4.024-.166C15.155 18.49 16 17.53 16 16.5v-3.75M12 8.25V12m-4.5 0H12m4.5 0H12" />
                                                        </svg>
                                                        {averageCpc}
                                                    </span>
                                                </div>
                                                {draft.status === 0 && (
                                                    <div className="flex gap-2 mt-2">
                                                        {processingTasks[draft.id] ? (
                                                            <div className="flex items-center gap-2">
                                                                <FaSpinner className="animate-spin text-gray-400" size={16} />
                                                                <span className="text-xs text-gray-400">{processingTasks[draft.id].message}</span>
                                                            </div>
                                                        ) : (
                                                            <>
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleAcceptReject(draft.id, 'accept');
                                                                    }}
                                                                    className="flex items-center gap-1 p-1 rounded hover:bg-green-500/20 transition-colors duration-200"
                                                                >
                                                                    <FaCheck className="text-green-400 hover:text-green-300" size={16} />
                                                                    <span className="text-xs text-green-400">Accept</span>
                                                                </button>
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleOpenFeedbackModal(draft.id);
                                                                    }}
                                                                    className="flex items-center gap-1 p-1 rounded hover:bg-red-500/20 transition-colors duration-200"
                                                                >
                                                                    <FaTimes className="text-red-400 hover:text-red-300" size={16} />
                                                                    <span className="text-xs text-red-400">Reject</span>
                                                                </button>
                                                            </>
                                                        )}
                                                    </div>
                                                )}
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        )}
                        {selectedDayDrafts.length === 0 && (
                            <div className="mt-4">
                                <p>No drafts for {currentDate.toLocaleDateString()}</p>
                            </div>
                        )}
                    </div>
                </div>
            ) : (
                <div className="w-full space-y-4 sm:space-y-6">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-0">
                        <div className="flex flex-col sm:flex-row items-center gap-4">
                            <h1 className="text-3xl font-bold text-white flex items-center gap-2">
                                Content Calendar
                            </h1>
                            <button
                                onClick={() => setIsHowItWorksOpen(true)}
                                className="flex items-center gap-2 px-3 py-1 text-sm bg-gray-700 text-white rounded hover:bg-gray-600 transition-all"
                            >
                                <IoMdInformationCircle className="text-white" />
                                How It Works
                            </button>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            <button
                                onClick={isPaid ? '' : handleUpgradeClick}
                                className={`px-4 py-2 font-semibold rounded-md
                    ${isPaid
                                    ? 'bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 hover:bg-gradient-to-br text-white'
                                    : 'bg-gradient-to-r from-purple-400 via-purple-500 to-purple-600 hover:bg-gradient-to-br text-white'
                                }`}
                            >
                                {isPaid
                                    ? <><FaStar className="inline mr-1" /> Pro</>
                                    : <><FaPlusCircle className="inline mr-1" /> Upgrade Now</>
                                }
                            </button>
                        </div>
                    </div>
                    <Banner
                        text="Manage your articles for upcoming dates. Articles are sent to your blog at 7PM-8AM EST."
                        onButtonClick={() => console.log("Upgrade Now clicked!")}
                    />
                    <div
                        ref={calendarRef}
                        className="h-[80vh] overflow-y-auto mt-4 bg-[#051829] rounded-lg p-4"
                    >
                        {monthsToRender.map((month) => renderMonth(month))}
                    </div>
                    <HowItWorksModal
                        isOpen={isHowItWorksOpen}
                        onClose={() => setIsHowItWorksOpen(false)}
                    />
                </div>
            )}
            {showFeedbackModal && (
                <div className="fixed inset-0 bg-gray-800 bg-opacity-75 flex justify-center items-center">
                    <div className="bg-gray-700 p-6 rounded-md shadow-lg w-11/12 sm:w-96">
                        <h2 className="text-xl font-bold mb-4 text-white">Provide Feedback</h2>
                        <textarea
                            className="w-full p-2 mb-4 rounded text-black"
                            rows="4"
                            placeholder="Enter your feedback here..."
                            value={feedbackText}
                            onChange={handleFeedbackChange}
                        />
                        <div className="flex justify-end gap-2">
                            <button
                                className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                                onClick={handleCloseFeedbackModal}
                            >
                                Cancel
                            </button>
                            <button className="px-4 py-2 bg-rose-500 text-white rounded hover:bg-rose-600" onClick={handleRejectWithFeedback}>
                                Submit Feedback
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Calendar;
